import React, { useEffect, useMemo, useRef, useState } from 'react';
import { doc, updateDoc, deleteDoc, getDoc, getDocs, collection, query, where, onSnapshot, limit, deleteField } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { fetchClientesDoUsuario } from '../../services/clientsQuery';
import { Client, clientDisplayName, sankhyaStatusInfo } from '../../types';
import { seloPendencias } from '../../utils/pendenciasEndereco';
import ClientFormModal from '../../components/ClientFormModal';
import CheckinModal from '../../components/CheckinModal';
import Pagination, { usePagination } from '../../components/Pagination';
import { useAuth } from '../../contexts/AuthContext';
import { useAccessScope } from '../../hooks/useAccessScope';

// Cache em MEMÓRIA por sessão (sobrevive à troca de aba dentro do SPA; some no reload — aí entra o
// cache persistente do Firestore). Chaveado por usuário+escopo+inativos.
const memCache = new Map<string, Client[]>();
const serverAt = new Map<string, number>(); // quando cada chave foi lida do SERVIDOR pela última vez
const CLIENTS_TTL_MS = 60_000;               // dentro disso, troca de aba NÃO re-sincroniza (espelha o TTL do app)

export default function ClientsPage() {
  const { user, isAdmin } = useAuth();
  const scope = useAccessScope();
  const [clients, setClients] = useState<Client[]>([]);
  const [firstPage, setFirstPage] = useState<Client[]>([]); // 1ª página instantânea (admin) enquanto a lista completa carrega
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Client | null>(null);
  const [checkinClient, setCheckinClient] = useState<Client | null>(null);
  const [canAddClient, setCanAddClient] = useState(true);
  const reqIdRef = useRef(0);      // ignora respostas antigas se o escopo/inativos mudar no meio
  const activeKeyRef = useRef(''); // chave do cache em memória da view atual

  // Carrega em DUAS FASES para não travar minutos no download da base:
  //  1) pinta INSTANTÂNEO do cache (memória da sessão → senão IndexedDB do Firestore);
  //  2) atualiza do servidor em segundo plano (delta barato com o cache persistente ligado).
  // O escopo é aplicado no servidor (vendedor/gestor baixam só a carteira; admin só ATIVOS).
  const loadClients = async () => {
    const key = `${user?.id ?? ''}|${scope.full ? 'full' : scopeKey}|${showInactive ? 'inc' : 'act'}`;
    activeKeyRef.current = key;
    const myReq = ++reqIdRef.current;

    // Fase 1 — pintura instantânea
    const mem = memCache.get(key);
    if (mem) {
      setClients(mem); setLoading(false); setRefreshing(true);
    } else {
      setLoading(true);
      try {
        const cached = (await fetchClientesDoUsuario(user, scope, { includeInactive: showInactive, fromCache: true })) as Client[];
        if (myReq !== reqIdRef.current) return; // escopo mudou → descarta
        if (cached.length) { setClients(cached); setLoading(false); setRefreshing(true); }
      } catch { /* sem cache local → segue no spinner até o servidor responder */ }
    }

    // Fase 2 — verdade do servidor, só se o cache estiver VELHO (evita rebaixar ~15k a cada troca de aba)
    const fresco = memCache.has(key) && (Date.now() - (serverAt.get(key) || 0)) < CLIENTS_TTL_MS;
    if (fresco) { if (myReq === reqIdRef.current) setRefreshing(false); return; }
    try {
      const fresh = (await fetchClientesDoUsuario(user, scope, { includeInactive: showInactive })) as Client[];
      if (myReq !== reqIdRef.current) return;
      memCache.set(key, fresh);
      serverAt.set(key, Date.now());
      setClients(fresh); // filtro por papel/escopo é reaplicado em `visibleClients` (defesa)
    } finally {
      if (myReq === reqIdRef.current) { setLoading(false); setRefreshing(false); }
    }
  };

  // Invalida o cache da view atual → a próxima carga busca o servidor (usar após criar/editar cliente,
  // pra o doc novo aparecer na hora em vez de esperar o TTL).
  const invalidateCache = () => { memCache.delete(activeKeyRef.current); serverAt.delete(activeKeyRef.current); };

  // Lista base: a COMPLETA quando já chegou; senão a 1ª página (admin, pintura instantânea).
  const baseList = clients.length ? clients : firstPage;
  // `parcial` = admin ainda mostrando só a 1ª página (busca/contagem incompletas até a lista chegar).
  const parcial = scope.full && clients.length === 0 && firstPage.length > 0;

  // Clientes visíveis conforme o papel: acesso total = todos; vendedor = os dele;
  // gestor = os dos vendedores da hierarquia dele (CODVENDs do escopo).
  const visibleClients = useMemo(() => {
    if (scope.full) return baseList;
    if (user?.role === 'seller') {
      return baseList.filter(c =>
        c.sellerId === user.id ||
        c.createdBy === user.id ||
        (user.codVendSankhya != null && String(c.sankhya?.codvend ?? '') === String(user.codVendSankhya))
      );
    }
    // Gestor: casa por CODVEND do cliente OU pelo sellerId (uid do vendedor da hierarquia) —
    // assim vê também clientes sem `sankhya.codvend` (cadastrados manualmente / não importados).
    const cvSet = new Set(scope.codvends || []);
    const idSet = new Set(scope.sellerIds || []);
    return baseList.filter(c => (c.sellerId != null && idSet.has(c.sellerId)) || cvSet.has(String(c.sankhya?.codvend ?? '')));
  }, [baseList, scope, user]);

  // Chave do escopo (equipeCodvends/sellerIds do gestor carregam async) — recarrega quando muda,
  // igual à lista do app. Sem isso, o gestor carregaria com escopo vazio e veria 0 clientes.
  const scopeKey = scope.full ? 'full' : `${(scope.codvends || []).join(',')}#${(scope.sellerIds || []).join(',')}`;

  useEffect(() => {
    // ADMIN (acesso total): 1ª PÁGINA INSTANTÂNEA + lista completa em 2º plano.
    //  (a) uma query pequena (`limit`) devolve ~120 clientes em milissegundos → a tela pinta NA HORA,
    //      mesmo no 1º acesso (cache frio) — sem esperar os ~15k.
    //  (b) a lista COMPLETA vem por `onSnapshot` (cache-first do IndexedDB nas próximas aberturas + só
    //      o DELTA do servidor) e habilita busca/paginação sobre tudo; mutações refletem ao vivo.
    if (scope.full) {
      const col = collection(db, 'clients');
      const baseQ = showInactive ? query(col) : query(col, where('active', '==', true));
      let cancelled = false;
      let unsub: (() => void) | null = null;

      // SEQUENCIAL de propósito: 1º a página pequena SOZINHA (o SDK processa os targets do stream e as
      // escritas do IndexedDB em fila — se o listener dos ~15k abrir junto, a query de 120 fica presa
      // atrás dele e a tela volta a demorar ~1min). Só depois que a 1ª página PINTOU é que o listener
      // completo arma, em 2º plano.
      const armarListenerCompleto = () => {
        if (cancelled) return;
        unsub = onSnapshot(
          baseQ,
          (snap) => {
            if (cancelled) return;
            setClients(snap.docs.map((d) => ({ id: d.id, ...d.data() })) as Client[]);
            setLoading(false);
            setRefreshing(snap.metadata.fromCache); // "atualizando…" enquanto o servidor não confirmou
          },
          (err) => { console.warn('Clientes onSnapshot:', err); setLoading(false); }
        );
      };

      getDocs(query(baseQ, limit(120)))
        .then((snap) => {
          if (cancelled) return;
          setFirstPage(snap.docs.map((d) => ({ id: d.id, ...d.data() })) as Client[]);
          setLoading(false); // já tem o que mostrar
        })
        .catch(() => {})
        .finally(() => {
          // deixa o browser RENDERIZAR a 1ª página antes de abrir a torneira dos 15k
          setTimeout(armarListenerCompleto, 300);
        });

      return () => { cancelled = true; if (unsub) unsub(); };
    }
    // Vendedor/gestor: carteira pequena — carga simples com cache-first (getDocs em 2 fases).
    loadClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, scopeKey, showInactive]);

  // Mantém o cache de sessão em dia com mutações locais (ativar/desativar, excluir, editar) — assim
  // voltar pra aba não mostra um estado velho antes do refresh.
  useEffect(() => {
    if (!loading && activeKeyRef.current) memCache.set(activeKeyRef.current, clients);
  }, [clients, loading]);

  useEffect(() => {
    // Configuração: vendedor pode cadastrar clientes?
    if (!isAdmin) {
      getDoc(doc(db, 'settings', 'app')).then(s => {
        if (s.exists()) setCanAddClient((s.data() as any).allowClientRegistrationBySeller !== false);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (client: Client) => { setEditing(client); setModalOpen(true); };
  // Admin: o listener onSnapshot já reflete o novo/editado na hora (não dispara getDocs de 15k).
  // Vendedor/gestor: recarrega a carteira (pequena).
  const handleSaved = () => { setModalOpen(false); if (!scope.full) { invalidateCache(); loadClients(); } };

  // Ao REATIVAR à mão, apaga `desativadoEm` — senão a exclusão por carência do Sincronizar (que busca
  // `desativadoEm <= corte`) apagaria de vez um cliente que o admin acabou de religar. O religamento
  // automático do Sincronizar já faz isso (functions/index.js:611); o manual não fazia.
  const toggleActive = async (client: Client) => {
    const novoAtivo = !client.active;
    await updateDoc(doc(db, 'clients', client.id), novoAtivo
      ? { active: true, desativadoEm: deleteField() }
      : { active: false });
    setClients(prev => prev.map(c => c.id === client.id ? { ...c, active: !c.active } : c));
  };

  const handleDelete = async (client: Client) => {
    const ok = window.confirm(`Excluir o cliente "${client.name}" permanentemente?\n\nEsta ação não pode ser desfeita. Os check-ins já registrados serão mantidos no histórico.`);
    if (!ok) return;
    await deleteDoc(doc(db, 'clients', client.id));
    setClients(prev => prev.filter(c => c.id !== client.id));
  };

  const codparcNum = (c: Client) => {
    const n = Number(c.sankhya?.codparc);
    return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY; // sem CODPARC vai para o fim
  };

  // Busca com debounce (250ms) + `filtered` memoizado: sem isso, cada tecla re-filtrava/re-ordenava
  // os ~15k clientes do admin a cada render — o que deixava a digitação travada.
  const [searchDebounced, setSearchDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  const filtered = useMemo(() => {
    const q = searchDebounced.toLowerCase();
    return visibleClients
      .filter(c => (
        c.name.toLowerCase().includes(q) ||
        (c.tradeName || '').toLowerCase().includes(q) ||
        (c.address?.city || '').toLowerCase().includes(q) ||
        String(c.sankhya?.codparc || '').includes(q) ||
        (c.cnpj || '').includes(q)
      ))
      .sort((a, b) => codparcNum(a) - codparcNum(b));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleClients, searchDebounced]);

  // Paginação (componente compartilhado — 25 linhas por página por padrão)
  const pager = usePagination(filtered, searchDebounced);
  const pageItems = pager.pageItems;

  return (
    <div>
      <div style={styles.pageHeader}>
        <div>
          <h1 style={styles.title}>{isAdmin ? 'Clientes' : 'Meus Clientes'}</h1>
          <p style={styles.subtitle}>
            {parcial
              ? <span style={styles.refreshing}>carregando lista completa…</span>
              : <>{visibleClients.filter(c => c.active).length} clientes ativos</>}
            {!parcial && refreshing && <span style={styles.refreshing}> · atualizando…</span>}
          </p>
        </div>
        {/* TODOS os perfis podem cadastrar cliente novo (o que é exclusivo do admin é EDITAR o
            cadastro). Acesso total sempre; os demais conforme a configuração do painel. */}
        {(isAdmin || canAddClient) && (
          <button style={styles.addBtn} onClick={openNew}>+ Novo Cliente</button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          style={{ ...styles.search, flex: 1, minWidth: 240 }}
          placeholder="Buscar por nome, código, CNPJ ou cidade..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {isAdmin && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#555', whiteSpace: 'nowrap', cursor: 'pointer' }}
                 title="Por padrão a lista mostra só os clientes ATIVOS (mais rápida). Marque para carregar também os desativados — mais lento.">
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
            Incluir inativos
          </label>
        )}
      </div>

      {loading ? (
        <p>Carregando...</p>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                {['Cliente', 'Cidade/UF', 'CNPJ', 'Vendedor', 'Status', 'Sankhya', 'Ações'].map(h => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageItems.map(client => (
                <tr key={client.id} style={styles.tr}>
                  <td style={styles.td}>
                    <strong>{clientDisplayName(client)}</strong>
                    {/* Pendências da verificação de cadastro SOMADAS à de endereço (CNEFE, `geoPendencia`),
                        que entra na dica como "rótulo: o que corrigir" — ver utils/pendenciasEndereco.ts. */}
                    {(() => {
                      const selo = seloPendencias(client);
                      return selo && (
                        <div title={selo.dica} style={styles.pendingFlag}>
                          🚩 {selo.total} pendência{selo.total > 1 ? 's' : ''} de cadastro
                        </div>
                      );
                    })()}
                  </td>
                  <td style={styles.td}>{client.address?.city} - {client.address?.state}</td>
                  <td style={styles.td}>{client.cnpj || client.cpf || '—'}</td>
                  <td style={styles.td}>{client.sellerName || '—'}</td>
                  <td style={styles.td}>
                    <span style={{ ...styles.badge, ...(client.active ? styles.badgeActive : styles.badgeInactive) }}>
                      {client.active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    {(() => {
                      const s = sankhyaStatusInfo(client.sankhya);
                      const title = client.sankhya?.error
                        || (client.sankhya?.status === 'nao_cadastrado' ? 'Cliente só do app — não sobe ao Sankhya'
                          : client.sankhya?.codparc ? `CODPARC ${client.sankhya.codparc}` : 'Ainda não sincronizado');
                      return (
                        <span title={title} style={{ ...styles.badge, color: s.color, background: s.bg }}>
                          {s.label}{client.sankhya?.codparc ? ` · ${client.sankhya.codparc}` : ''}
                        </span>
                      );
                    })()}
                  </td>
                  <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                    {!isAdmin && (
                      <button style={styles.checkinBtn} onClick={() => setCheckinClient(client)}>📍 Check-in/out</button>
                    )}
                    {/* Só o admin edita o cadastro; os demais abrem a mesma ficha em somente leitura. */}
                    <button style={styles.actionBtn} onClick={() => openEdit(client)}>
                      {user?.role === 'admin' ? 'Editar' : 'Ver'}
                    </button>
                    {/* Ativar/Desativar e Excluir são do papel LITERAL `admin` — `isAdmin` do contexto é
                        isFullAccess e incluiria diretor/pricing/GN, que a regra já recusa (erro na tela). */}
                    {user?.role === 'admin' && (
                      <>
                        <button style={styles.actionBtn} onClick={() => toggleActive(client)}>
                          {client.active ? 'Desativar' : 'Ativar'}
                        </button>
                        <button style={styles.deleteBtn} onClick={() => handleDelete(client)}>Excluir</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <p style={{ textAlign: 'center', color: '#999', padding: 32 }}>
              {parcial ? 'Buscando na lista completa…' : 'Nenhum cliente encontrado.'}
            </p>
          )}
        </div>
      )}

      {!loading && <Pagination pager={pager} label="clientes" />}

      {modalOpen && (
        <ClientFormModal
          client={editing}
          onClose={() => setModalOpen(false)}
          onSaved={handleSaved}
        />
      )}

      {checkinClient && (
        <CheckinModal
          client={checkinClient}
          onClose={() => setCheckinClient(null)}
          onDone={() => { setCheckinClient(null); }}
        />
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  title: { fontSize: 24, fontWeight: 700, color: '#1a1a2e', margin: 0 },
  subtitle: { color: '#666', margin: '4px 0 0', fontSize: 14 },
  refreshing: { color: '#f5821f', fontWeight: 600 },
  addBtn: { background: '#f5821f', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 14 },
  search: { width: '100%', border: '1px solid #e0e0e0', borderRadius: 10, padding: '10px 14px', fontSize: 15, marginBottom: 20, boxSizing: 'border-box', background: '#fff' },
  tableWrapper: { background: '#fff', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 760 },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#666', textTransform: 'uppercase', borderBottom: '2px solid #f0f0f0', background: '#fafafa', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f5f5f5' },
  td: { padding: '12px 16px', fontSize: 14, color: '#333' },
  badge: { padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
  badgeActive: { background: '#dcfce7', color: '#16a34a' },
  badgeInactive: { background: '#f3f4f6', color: '#6b7280' },
  pendingFlag: { marginTop: 4, fontSize: 11, fontWeight: 600, color: '#b45309', cursor: 'help' },
  actionBtn: { background: 'none', border: '1px solid #e0e0e0', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', marginRight: 6, color: '#555' },
  deleteBtn: { background: 'none', border: '1px solid #fecaca', borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: '#dc2626' },
  checkinBtn: { background: '#f5821f', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer', marginRight: 6, fontWeight: 600 },
};
